/**
 * Customer Sites — physical customer locations with delivery/pickup instructions.
 * Small MVP: list, add, activate/deactivate. Full serviceability editing lives in Edit dialog.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Building2, ArrowLeft, MapPin, Check, X } from "lucide-react";
import { AddressPicker } from "@/components/app/AddressPicker";

export default function CustomerSites() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [masters, setMasters] = useState([]);

  const load = () => api.get("/sites").then(({ data }) => setItems(data.items || []));
  useEffect(() => {
    load();
    api.get("/master-companies").then(({ data }) => setMasters(data.items || [])).catch(() => {});
  }, []);

  return (
    <div className="max-w-5xl space-y-6" data-testid="customer-sites">
      <Link to="/admin/control-centre" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Control Centre
      </Link>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Organisation</div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Customer sites</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Physical delivery and pickup locations for each customer, with business hours, contacts and courier-ready instructions.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1" data-testid="site-add">
          <Plus className="h-4 w-4" /> Add site
        </button>
      </div>

      {showForm && <SiteForm masters={masters} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No sites yet. Add one to unlock delivery and pickup workflows.</div>
        ) : items.map((s) => (
          <div key={s.id} className="p-4 flex items-start gap-4" data-testid={`site-row-${s.id}`}>
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center shrink-0">
              <Building2 className="h-4 w-4" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{s.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {s.site_type} · {s.city || "—"}, {s.state_code || s.state || "—"} · {s.country}
              </div>
              {s.address_line1 && <div className="text-xs text-slate-600 mt-1"><MapPin className="h-3 w-3 inline mr-1" />{s.address_line1}{s.postal_code ? ` · ${s.postal_code}` : ""}</div>}
              <div className="mt-2 flex flex-wrap gap-1">
                <ServiceBadge label="Forward" ok={s.serviceable_forward} />
                <ServiceBadge label="Reverse" ok={s.serviceable_reverse} />
                <ServiceBadge label="Same-day" ok={s.serviceable_same_day} />
                <ServiceBadge label="Onsite" ok={s.serviceable_onsite} />
                <ServiceBadge label="ITAD pickup" ok={s.serviceable_itad_pickup} />
              </div>
            </div>
            <SiteToggle site={s} onChanged={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ServiceBadge({ label, ok }) {
  return (
    <span className={
      "text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border inline-flex items-center gap-1 " +
      (ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-50 border-slate-200")
    }>
      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {label}
    </span>
  );
}

function SiteToggle({ site, onChanged }) {
  const toggle = async () => {
    try {
      await api.patch(`/sites/${site.id}`, { active: !site.active });
      toast.success(!site.active ? "Site activated" : "Site deactivated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <button onClick={toggle} data-testid={`site-toggle-${site.id}`}
      className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " +
        (site.active ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-50 border-slate-200")}>
      {site.active ? "Active" : "Inactive"}
    </button>
  );
}

function SiteForm({ masters, onCancel, onCreated }) {
  const [f, setF] = useState({
    master_company_id: masters[0]?.id || "",
    name: "",
    site_type: "HEAD_OFFICE",
    country: "IN",
    address_line1: "", city: "", state: "", state_code: "", postal_code: "",
    timezone: "Asia/Kolkata",
    business_hours: "09:00-18:00",
    delivery_instructions: "",
    pickup_instructions: "",
    serviceable_forward: true,
    serviceable_reverse: true,
    serviceable_same_day: false,
    serviceable_onsite: true,
    serviceable_itad_pickup: true,
    do_not_ship_policy: {
      require_employee_verified: true,
      require_address_verified: true,
      require_primary_contact: true,
      require_secondary_contact: false,
      require_courier_serviceable: true,
      require_device_qc_complete: true,
      require_mdm_complete: false,
      require_customer_approval: false,
    },
  });
  const [addr, setAddr] = useState({});

  const save = async () => {
    try {
      const payload = { ...f, ...addr };
      // AddressPicker uses "locality" — keep as address_line2 when saving to site
      if (!payload.master_company_id) return toast.error("Select a master company");
      if (!payload.name) return toast.error("Site name is required");
      await api.post("/sites", payload);
      toast.success("Site created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="site-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Master company</div>
          <select className="input" value={f.master_company_id} onChange={(e) => setF({ ...f, master_company_id: e.target.value })} data-testid="site-mc">
            <option value="">—</option>
            {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Site name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="site-name" />
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Site type</div>
          <select className="input" value={f.site_type} onChange={(e) => setF({ ...f, site_type: e.target.value })}>
            {["HEAD_OFFICE","BRANCH","REMOTE","DATA_CENTRE","WAREHOUSE","COWORK","CUSTOMER_LOC","PROJECT","OTHER"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
      </div>
      <AddressPicker value={addr} onChange={setAddr} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Business hours</div>
          <input className="input" value={f.business_hours} onChange={(e) => setF({ ...f, business_hours: e.target.value })} />
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Timezone</div>
          <input className="input" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} />
        </label>
        <label className="md:col-span-2">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Delivery instructions</div>
          <textarea className="input min-h-[60px]" value={f.delivery_instructions} onChange={(e) => setF({ ...f, delivery_instructions: e.target.value })} placeholder="Call reception on arrival, security clearance required, floor 4 IT desk…" />
        </label>
        <label className="md:col-span-2">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Pickup instructions</div>
          <textarea className="input min-h-[60px]" value={f.pickup_instructions} onChange={(e) => setF({ ...f, pickup_instructions: e.target.value })} placeholder="Original packaging held at reception, call before pickup…" />
        </label>
      </div>
      <div className="flex flex-wrap gap-4">
        {["forward","reverse","same_day","onsite","itad_pickup"].map((k) => (
          <label key={k} className="text-xs text-slate-700 inline-flex items-center gap-2">
            <input type="checkbox" checked={f[`serviceable_${k}`]} onChange={(e) => setF({ ...f, [`serviceable_${k}`]: e.target.checked })} />
            Serviceable · {k.replace("_", " ")}
          </label>
        ))}
      </div>

      <details className="border border-slate-200 rounded-md" data-testid="dns-editor">
        <summary className="px-3 py-2 text-sm font-semibold text-slate-800 cursor-pointer bg-slate-50">Do-Not-Ship policy</summary>
        <div className="p-3 space-y-2">
          <p className="text-xs text-slate-500">Block dispatch when any required condition is not satisfied.</p>
          {Object.entries(f.do_not_ship_policy || {}).map(([k, v]) => (
            <label key={k} className="text-xs text-slate-700 inline-flex items-center gap-2 mr-4">
              <input type="checkbox" checked={!!v}
                onChange={(e) => setF({ ...f, do_not_ship_policy: { ...(f.do_not_ship_policy||{}), [k]: e.target.checked } })}
                data-testid={`dns-${k}`} />
              {k.replace("require_", "").replaceAll("_", " ")}
            </label>
          ))}
        </div>
      </details>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="h-9 px-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
        <button onClick={save} className="h-9 px-3 text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-md" data-testid="site-save">Create site</button>
      </div>
    </div>
  );
}
